import React, { useEffect } from "react";
import { useCreateEmailTemplateStore } from "../../../stores/Hooks";
import { Stack, Typography } from "@mui/material";
import { DividerComponent } from "../../../../surveys/components/commonQuestionConfigurationDetailsComponents/DividerComponent";
import { useTranslation } from "react-i18next";
import {
  Button,
  IconButton,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { X } from "lucide-react";
import { CreateEmailTemplateForm } from "../../forms/CreateEmailTemplateForm";
import { CreateEmailTemplateRPC } from "@pulse/pulse-rpcs/lib/email-cycle/createEmailTemplateRPC";
import { observer } from "mobx-react";
import { useRouteNavigator } from "../../../../../routes/RoutesHelper";

interface CreateEmailTemplateDrawerContentProps {
  handleDrawerClose: VoidFunction;
  setOpenSuccessMessageDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenErrorMessageDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenProjectArchivedErrorMessageDialog: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

export const CreateEmailTemplateDrawerContent: React.FC<CreateEmailTemplateDrawerContentProps> =
  observer(
    ({
      handleDrawerClose,
      setOpenSuccessMessageDialog,
      setOpenErrorMessageDialog,
      setOpenProjectArchivedErrorMessageDialog,
    }) => {
      const { t } = useTranslation();
      const tokens = useFoundationColorTokens();
      const typography = useTypography();
      const spacing = useSpacing();
      const navigateTo = useRouteNavigator();
      const createEmailTemplateStore = useCreateEmailTemplateStore();

      useEffect(() => createEmailTemplateStore.resetStore, []);

      const handleCreateEmailTemplate = async (): Promise<void> => {
        createEmailTemplateStore.setProjectId(null);
        await createEmailTemplateStore.createEmailTemplate().catch(() => {
          handleDrawerClose();
          setOpenErrorMessageDialog(true);
        });

        if (createEmailTemplateStore.disableSubmit) {
          return;
        }

        switch (createEmailTemplateStore.createEmailTemplateRPCError) {
          case null:
            setOpenSuccessMessageDialog(true);
            break;
          case CreateEmailTemplateRPC.RPCError.InvalidProjectId:
            navigateTo.pageNotFound();
            break;
          case CreateEmailTemplateRPC.RPCError.ProjectAlreadyArchived:
            setOpenProjectArchivedErrorMessageDialog(true);
            break;
          default:
            createEmailTemplateStore.createEmailTemplateRPCError satisfies never;
        }

        handleDrawerClose();
      };

      return (
        <Stack
          divider={<DividerComponent orientation="horizontal" width="100%" />}
          height="100%"
        >
          {/* Drawer Header */}
          <Stack
            direction="row"
            paddingBlock={spacing.spaceMD}
            paddingInline={spacing.space2XL}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography color={tokens.labelHighEmphasis} {...typography.sh2}>
              {t("emailTemplates.createEmailTemplate")}
            </Typography>
            <IconButton
              name="createEmailTemplateDrawerCloseButton"
              size="medium"
              icon={<X />}
              variant="plain-neutral"
              onClick={handleDrawerClose}
            />
          </Stack>

          {/* Drawer Body */}
          <Stack
            padding={spacing.space2XL}
            flex={1}
            gap={spacing.spaceXL}
            overflow="auto"
          >
            <Typography color={tokens.labelHighEmphasis} {...typography.sh2}>
              {t("emailTemplates.messageCustomization")}
            </Typography>
            <CreateEmailTemplateForm />
          </Stack>

          {/* Drawer Footer */}
          <Stack
            alignItems="end"
            paddingBlock={spacing.spaceMD}
            paddingInline={spacing.space2XL}
          >
            <Button
              name="createEmailTemplateButton"
              label={t("emailTemplates.createEmailTemplate")}
              variant="filled"
              size="medium"
              onClick={handleCreateEmailTemplate}
              disabled={createEmailTemplateStore.disableSubmit}
            />
          </Stack>
        </Stack>
      );
    },
  );
